import { useContext } from "react";
import { useSelector } from "react-redux";
import logger from "logging/logger";
import { useAppDispatch } from "store/hooks";
import { useI18n } from "i18n";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import { usePopulateEventData } from "modules/event/hooks/apiHooks";
import { selectMyCompany } from "modules/company/redux/selectors";
import { Actions, trackAction } from "modules/monitoring";
import { Events, TRACKING_CONTEXT } from "modules/tracking";
import * as EventDirectoryAPI from "../api";
import { loadCompanyDirectories } from "../redux/slice";
import { selectCompanyEventDirectories } from "../redux/selectors";
import { IEventDirectory, IEventDirectoryUpsertData } from "../types";

const useCompanyEventDirectory = () => {
  const dispatch = useAppDispatch();
  const { addSuccessNotification, addErrorNotification } =
    useNotificationActions();
  const { getUpcomingMyEvents, getPastMyEvents, getOngoingMyEvents } =
    usePopulateEventData();
  const { t } = useI18n(["eventDirectory", "server"]);
  const company = useSelector(selectMyCompany);
  const companyEventDirectories = useSelector(selectCompanyEventDirectories);
  const { track } = useContext(TRACKING_CONTEXT);

  // after directory create/update/delete, store the new directory array and
  // update created events in store
  const updateStore = async (eventDirectories: IEventDirectory[]) => {
    dispatch(loadCompanyDirectories(eventDirectories));

    if (!company) {
      addErrorNotification({
        message: t("server:contact.us.error.persists"),
      });

      return;
    }

    await Promise.all([
      getOngoingMyEvents(company.id),
      getUpcomingMyEvents(company.id),
      getPastMyEvents(company.id),
    ]);
  };

  const getCompanyEventDirectories = async () => {
    if (!company) {
      return;
    }

    const response = await EventDirectoryAPI.getCompanyEventDirectories(
      company.id,
    );

    if (!response.isSuccess) {
      addErrorNotification({
        message: t("eventDirectory:fetch.error", {
          error: response.message,
        }),
      });
      logger.error(
        `[getCompanyEventDirectories] Error occurred while fetching event directory: ${response.message}`,
      );

      return;
    }

    logger.info("[getCompanyEventDirectories] Fetched event directory", {
      eventDirectory: response.eventDirectories,
    });
    dispatch(loadCompanyDirectories(response.eventDirectories));
  };

  const createCompanyEventDirectory = async (
    data: IEventDirectoryUpsertData,
  ) => {
    if (!company) {
      return;
    }

    logger.info("[createCompanyEventDirectory] Creating event directory...", {
      data,
    });

    const response = await EventDirectoryAPI.createEventDirectory(
      company.id,
      data,
    );

    if (!response.isSuccess) {
      logger.error(
        "[createCompanyEventDirectory] Error occurred while creating event directory",
      );
      addErrorNotification({
        message: t("eventDirectory:create.error", {
          error: response.message,
        }),
      });

      return;
    }

    logger.info("[createCompanyEventDirectory] Event directory created", {
      eventDirectory: response.eventDirectories,
    });
    await updateStore(response.eventDirectories);
    addSuccessNotification({
      message: t("eventDirectory:create.success", {
        name: data.name,
      }),
    });

    const metadata = {
      name: data.name,
      count: response.eventDirectories.length,
    };

    track(Events.DIRECTORY_CREATED, metadata);
    trackAction(Actions.DIRECTORY_CREATED, metadata);

    // eslint-disable-next-line consistent-return
    return true;
  };

  const updateCompanyEventDirectory = async (
    directoryId: string,
    data: IEventDirectoryUpsertData,
  ) => {
    if (!company) {
      return;
    }

    logger.info(
      `[updateCompanyEventDirectory] Updating event directory ${directoryId}`,
      {
        data,
      },
    );

    const response = await EventDirectoryAPI.updateEventDirectory(
      company.id,
      directoryId,
      data,
    );

    if (!response.isSuccess) {
      logger.error(
        `[updateCompanyEventDirectory] Error occurred while updating event directory ${directoryId}`,
      );
      addErrorNotification({
        message: t("eventDirectory:update.error", {
          error: response.message,
        }),
      });

      // eslint-disable-next-line consistent-return
      return false;
    }

    logger.info(
      `[updateCompanyEventDirectory] Event directory ${directoryId} updated`,
      {
        eventDirectory: response.eventDirectories,
      },
    );
    await updateStore(response.eventDirectories);
    addSuccessNotification({
      message: t("eventDirectory:update.success", {
        name: data.name,
      }),
    });

    const metadata = {
      name: data.name,
      count: response.eventDirectories.length,
    };

    track(Events.DIRECTORY_UPDATED, metadata);
    trackAction(Actions.DIRECTORY_UPDATED, metadata);

    // eslint-disable-next-line consistent-return
    return true;
  };

  const deleteCompanyEventDirectory = async (directoryId: string) => {
    if (!company) {
      return;
    }

    logger.info(
      `[deleteCompanyEventDirectory] Deleting event directory ${directoryId}`,
    );

    const response = await EventDirectoryAPI.deleteEventDirectory(
      company.id,
      directoryId,
    );

    if (!response.isSuccess) {
      logger.error(
        `[deleteCompanyEventDirectory] Error occurred while deleting event directory ${directoryId}`,
      );
      addErrorNotification({
        message: t("eventDirectory:delete.error", {
          error: response.message,
        }),
      });

      return;
    }

    logger.info(
      `[deleteCompanyEventDirectory] Event directory ${directoryId} deleted`,
    );
    await updateStore(response.eventDirectories);
    addSuccessNotification({
      message: t("eventDirectory:delete.success"),
    });

    track(Events.DIRECTORY_DELETED, {
      count: response.eventDirectories.length,
    });
    trackAction(Actions.DIRECTORY_DELETED, {
      count: response.eventDirectories.length,
    });
  };

  return {
    companyEventDirectories,
    getCompanyEventDirectories,
    createCompanyEventDirectory,
    updateCompanyEventDirectory,
    deleteCompanyEventDirectory,
  };
};

export default useCompanyEventDirectory;
